import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isMounted = useRef(true);
  const profileUnsubRef = useRef(null);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Run one-time migration then start real-time onSnapshot listener
  const startProfileListener = useCallback((user) => {
    if (profileUnsubRef.current) {
      profileUnsubRef.current();
      profileUnsubRef.current = null;
    }

    const userProfileRef = doc(db, 'userProfiles', user.uid);
    const usersRef = doc(db, 'users', user.uid);

    // Run migration once, then attach real-time listener
    const runMigrationAndListen = async () => {
      try {
        const userProfileSnap = await getDoc(userProfileRef);

        if (!userProfileSnap.exists()) {
          // Try fallback users collection
          const usersSnap = await getDoc(usersRef);
          const baseData = usersSnap.exists() ? usersSnap.data() : {};

          const newProfile = {
            email: user.email || '',
            fullName: user.displayName || 'User',
            isAdmin: false,
            canWin: true,
            balance: 0,
            cryptoBalances: { USDT: 0, USDC: 0, BTC: 0, ETH: 0, SOL: 0, XRP: 0 },
            accountBalances: baseData.accountBalances || {
              funding: baseData.cryptoBalances || {},
              trading: {},
              arbitrage: {}
            },
            transactions: baseData.transactions || [],
            verification_status: 'unverified',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            ...baseData
          };

          await setDoc(userProfileRef, newProfile, { merge: true });
          if (!usersSnap.exists()) {
            await setDoc(usersRef, newProfile, { merge: true });
          }

          // Ensure the `index` collection exists (vibe-coder convenience).
          // Creates `index/{uid}` on first sign-in; safe to run repeatedly.
          try {
            await setDoc(
              doc(db, 'index', user.uid),
              {
                uid: user.uid,
                email: user.email || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          } catch (idxErr) {
            console.warn('Could not write index document:', idxErr?.message);
          }
        } else {
          // Migration: add missing fields for old users
          const data = userProfileSnap.data();
          const patch = {};
          if (!data.accountBalances) {
            patch.accountBalances = {
              funding: data.cryptoBalances || {},
              trading: {},
              arbitrage: {}
            };
          }
          if (!data.transactions) {
            patch.transactions = [];
          }
          if (data.canWin === undefined || data.canWin === null) {
            patch.canWin = true;
          }
          if (Object.keys(patch).length > 0) {
            patch.updatedAt = serverTimestamp();
            await setDoc(userProfileRef, patch, { merge: true });
          }
        }
      } catch (err) {
        console.error('Migration error:', err);
      }

      // Now attach real-time listener — fires immediately with current data
      const unsubscribe = onSnapshot(
        userProfileRef,
        (snap) => {
          if (!isMounted.current) return;
          if (snap.exists()) {
            setUserData(snap.data());
          }
          setLoading(false);
          setConnectionError(null);
        },
        (err) => {
          console.error('Profile listener error:', err);
          if (!isMounted.current) return;
          setConnectionError('Failed to load user data. Please check your connection.');
          setLoading(false);
        }
      );

      profileUnsubRef.current = unsubscribe;
    };

    runMigrationAndListen();
  }, []);

  useEffect(() => {
    isMounted.current = true;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!isMounted.current) return;
      setCurrentUser(user);

      if (user) {
        setLoading(true);
        startProfileListener(user);
      } else {
        if (profileUnsubRef.current) {
          profileUnsubRef.current();
          profileUnsubRef.current = null;
        }
        setUserData(null);
        setConnectionError(null);
        setLoading(false);
      }
    });

    return () => {
      isMounted.current = false;
      unsubAuth();
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }
    };
  }, [startProfileListener]);

  const refreshUserData = useCallback(() => {
    if (currentUser) startProfileListener(currentUser);
  }, [currentUser, startProfileListener]);

  const value = {
    currentUser,
    userData,
    loading,
    connectionError,
    isOnline,
    refreshUserData,
    signOut: useCallback(() => signOut(auth), [])
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
